// Bulletproof reminder reconciliation (Walter 2026-06-16).
//
// The old capture path diffed bell lines by content HASH and only updated a
// reminder doc on an exactly-one-removed-one-added edit. That's fragile:
// near-duplicate lines, multi-line edits, or a capture firing mid-typing
// left docs with STALE text (e.g. "call tom a" when the line said "call tom
// again"), or produced orphans / duplicates, or mis-sorted on organize.
//
// This replaces the incremental diff with a FULL reconciliation: given the
// CURRENT bell lines and the EXISTING reminder docs for a list, decide which
// doc maps to which line, which lines are new, and which docs are stale —
// such that afterwards every line has exactly one doc whose text/date match
// it, removed lines' docs are deleted, and doc identity (id → notes/history/
// snooze) is preserved wherever a line clearly maps to a doc.
//
// Matching is two-pass and deterministic:
//   1. EXACT: line.hash === doc.sourceLineHash (unchanged lines).
//   2. SIMILAR: among the remainder, pair the most-similar line/doc pairs
//      above a threshold (catches in-place edits like "call tom a" →
//      "call tom again" without mis-pairing unrelated delete+add).
// Leftover lines → create; leftover docs → delete.
//
// PURE + side-effect-free so it can be unit-tested against the exact bug
// scenarios. The hook builds Firestore payloads from the decisions.

// Token-set Jaccard with a shared-prefix bonus. 0 (nothing alike) → 1
// (identical). Robust to word add/remove (the common edit) and to the
// "typed a prefix, then finished the word" case (mid-typing capture).
export function reminderTextSimilarity(a, b) {
  const sa = (a || '').trim().toLowerCase();
  const sb = (b || '').trim().toLowerCase();
  if (!sa && !sb) return 1;
  if (!sa || !sb) return 0;
  if (sa === sb) return 1;

  const ta = sa.split(/\s+/).filter(Boolean);
  const tb = sb.split(/\s+/).filter(Boolean);
  const setA = new Set(ta);
  const setB = new Set(tb);
  let inter = 0;
  for (const t of setA) if (setB.has(t)) inter += 1;
  const union = new Set([...ta, ...tb]).size || 1;
  const jaccard = inter / union;

  // Prefix bonus: one string being a leading substring of the other is a
  // very strong "same line, mid-edit" signal ("call tom a" ⊂ "call tom
  // again"). Scale by how much of the longer string is shared.
  const longer = sa.length >= sb.length ? sa : sb;
  const shorter = sa.length >= sb.length ? sb : sa;
  const prefixBonus = longer.startsWith(shorter) ? (shorter.length / longer.length) : 0;

  return Math.max(jaccard, prefixBonus);
}

/**
 * reconcileReminders(existingDocs, currentLines, opts)
 *
 * @param {Array<{id, sourceLineHash, text}>} existingDocs  docs for ONE list
 * @param {Array<{hash, text}>} currentLines  current bell lines, doc order
 * @param {Object} [opts]
 * @param {number} [opts.threshold=0.5]  min similarity to treat as an edit
 *                                       of the same line (else create/delete)
 * @returns {{
 *   matches: Array<{docId, lineIndex, reason: 'hash'|'similar'}>,
 *   newLineIndexes: number[],
 *   staleDocIds: Array<string>,
 * }}
 */
export function reconcileReminders(existingDocs = [], currentLines = [], opts = {}) {
  const threshold = typeof opts.threshold === 'number' ? opts.threshold : 0.5;
  const docs = Array.isArray(existingDocs) ? existingDocs : [];
  const lines = Array.isArray(currentLines) ? currentLines : [];

  const usedDoc = new Set();   // doc ids already matched
  const usedLine = new Set();  // line indexes already matched
  const matches = [];

  // ── Pass 1: exact hash. A doc whose hash equals a line's hash is the
  // same, unchanged line. First-come pairing (handles true duplicates: two
  // identical lines with two identical-hash docs each grab one). ──
  const docsByHash = new Map();
  for (const d of docs) {
    if (!d || d.sourceLineHash == null) continue;
    if (!docsByHash.has(d.sourceLineHash)) docsByHash.set(d.sourceLineHash, []);
    docsByHash.get(d.sourceLineHash).push(d);
  }
  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    const bucket = docsByHash.get(ln && ln.hash);
    if (!bucket || bucket.length === 0) continue;
    const d = bucket.shift();
    usedDoc.add(d.id);
    usedLine.add(i);
    matches.push({ docId: d.id, lineIndex: i, reason: 'hash' });
  }

  // ── Pass 2: similarity over the remainder. Score every (line, doc) pair,
  // keep those above threshold, then greedily assign best-first so each doc
  // and line is used at most once. This is what catches an in-place edit
  // (hash changed) without mis-pairing an unrelated delete + add (their
  // similarity is ~0, below threshold). ──
  const candidates = [];
  for (let i = 0; i < lines.length; i++) {
    if (usedLine.has(i)) continue;
    const lineText = (lines[i] && lines[i].text) || '';
    for (const d of docs) {
      if (usedDoc.has(d.id)) continue;
      const s = reminderTextSimilarity(lineText, d.text || '');
      if (s >= threshold) candidates.push({ lineIndex: i, docId: d.id, score: s });
    }
  }
  // Best score first; ties broken deterministically by line then doc id so
  // the result is stable across runs (no Math.random / Date).
  candidates.sort((a, b) =>
    b.score - a.score
    || a.lineIndex - b.lineIndex
    || String(a.docId).localeCompare(String(b.docId)));
  for (const c of candidates) {
    if (usedLine.has(c.lineIndex) || usedDoc.has(c.docId)) continue;
    usedLine.add(c.lineIndex);
    usedDoc.add(c.docId);
    matches.push({ docId: c.docId, lineIndex: c.lineIndex, reason: 'similar' });
  }

  // Leftovers.
  const newLineIndexes = [];
  for (let i = 0; i < lines.length; i++) if (!usedLine.has(i)) newLineIndexes.push(i);
  const staleDocIds = [];
  for (const d of docs) if (!usedDoc.has(d.id)) staleDocIds.push(d.id);

  return { matches, newLineIndexes, staleDocIds };
}
